import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { 
  HelpCircle, Book, MessageCircle, Play, Search, 
  ChevronRight, ExternalLink, Mail, Lightbulb,
  LayoutDashboard, ArrowLeftRight, Target, PiggyBank,
  TrendingUp, Tags, Wallet, Upload, Globe, Landmark,
  Bell, CreditCard, Settings, Shield, Smartphone,
  Calculator, FileText, Brain, Mic, Users, Lock,
  Check, Star, Zap
} from 'lucide-react';
import { cn } from '../lib/utils';

const HelpCenter = () => {
  const { user, language } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSection, setActiveSection] = useState('getting-started');

  // Manual de usuario completo
  const userManual = {
    'getting-started': {
      title: 'Primeros Pasos',
      icon: Play,
      sections: [
        {
          title: '¿Qué es TREBOL Finance?',
          content: `TREBOL Finance es tu ERP financiero personal. Una aplicación completa para gestionar 
          todas tus finanzas personales en un solo lugar: ingresos, gastos, presupuestos, inversiones, 
          metas de ahorro y mucho más.
          
          **Principio fundamental:** "Ningún euro entra o sale sin quedar registrado, categorizado, 
          analizado y comprendido."`,
        },
        {
          title: 'Configuración inicial',
          content: `Al crear tu cuenta, el asistente de configuración te guiará:
          
          1. **Selecciona tu moneda** - EUR, USD, GBP y 12 más
          2. **Crea tus cuentas** - Banco principal, efectivo, tarjetas
          3. **Configura ingresos** - Nómina y otros ingresos recurrentes
          4. **Tu primer presupuesto** - Empieza controlando una categoría
          
          Puedes saltar cualquier paso y configurarlo después.`,
        },
        {
          title: 'Navegación principal',
          content: `El menú lateral te da acceso a todas las funciones:
          
          • **Panel de Control** - Vista general de tus finanzas
          • **Transacciones** - Todos tus movimientos
          • **Presupuestos** - Control de gastos por categoría
          • **Metas de Ahorro** - Objetivos con gamificación
          • **Inversiones** - Seguimiento de tu cartera
          • **Informes** - Análisis detallados
          
          En móvil, usa el menú hamburguesa (☰) para acceder.`,
        },
      ],
    },
    'transactions': {
      title: 'Transacciones',
      icon: ArrowLeftRight,
      sections: [
        {
          title: 'Registrar una transacción',
          content: `Para añadir un gasto o ingreso:
          
          1. Ve a **Transacciones** → **+ Nueva Transacción**
          2. Selecciona el tipo: Ingreso o Gasto
          3. Introduce el importe
          4. Selecciona la cuenta y categoría
          5. Añade una descripción (opcional)
          6. Guarda
          
          **Tip:** Puedes escanear un recibo con la cámara para rellenar automáticamente.`,
        },
        {
          title: 'Transacciones recurrentes',
          content: `Configura pagos automáticos que se repiten:
          
          1. Ve a **Recurrentes** → **+ Nueva**
          2. Configura: importe, frecuencia (diaria, semanal, mensual, anual)
          3. Fecha de inicio y fin (opcional)
          4. La app creará las transacciones automáticamente
          
          **Ejemplos:** Nómina, alquiler, Netflix, Spotify, seguro del coche...`,
        },
        {
          title: 'Importar desde el banco',
          content: `Importa cientos de transacciones en segundos:
          
          1. Descarga el extracto de tu banco (CSV u OFX)
          2. Ve a **Importar Banco**
          3. Sube el archivo y selecciona la cuenta destino
          4. Revisa la vista previa
          5. Selecciona las transacciones a importar
          6. ¡Listo!
          
          **Bancos soportados:** Santander, BBVA, CaixaBank, ING, N26, Revolut y más.`,
        },
        {
          title: 'Escanear recibos (OCR)',
          content: `Usa la cámara para digitalizar recibos:
          
          1. Pulsa el botón de cámara 📷 en el Dashboard
          2. Toma una foto del ticket o sube una imagen
          3. La IA extraerá: comercio, importe, fecha
          4. Revisa y confirma los datos
          5. Se crea la transacción automáticamente
          
          **Funciona con:** Tickets de supermercado, facturas, recibos...`,
        },
      ],
    },
    'budgets': {
      title: 'Presupuestos',
      icon: Target,
      sections: [
        {
          title: 'Crear un presupuesto',
          content: `Controla cuánto gastas en cada categoría:
          
          1. Ve a **Presupuestos** → **+ Nuevo Presupuesto**
          2. Selecciona la categoría (ej: Alimentación)
          3. Define el límite mensual (ej: 400€)
          4. Guarda
          
          Verás una barra de progreso que muestra cuánto llevas gastado.`,
        },
        {
          title: 'Alertas de presupuesto',
          content: `Recibe avisos antes de pasarte:
          
          - **80%** - Aviso: "Vas por buen camino, pero cuidado"
          - **100%** - Alerta: "Has alcanzado el límite"
          - **>100%** - Crítico: "Te has pasado del presupuesto"
          
          Configura las alertas en **Alertas** → **Configuración**.`,
        },
        {
          title: 'Consejos para presupuestar',
          content: `• **Empieza simple** - 3-4 categorías principales
          • **Sé realista** - Mira tu historial antes de fijar límites
          • **Revisa mensualmente** - Ajusta según necesidades
          • **Usa la regla 50/30/20** - Necesidades/Deseos/Ahorro
          • **No te castigues** - Es una guía, no una restricción`,
        },
      ],
    },
    'savings': {
      title: 'Metas de Ahorro',
      icon: PiggyBank,
      sections: [
        {
          title: 'Sistema de gamificación',
          content: `TREBOL convierte el ahorro en un juego:
          
          • **XP (Puntos de experiencia)** - Gana XP cada vez que ahorras
          • **Niveles** - Sube de nivel cada 500 XP
          • **Badges** - Desbloquea logros especiales
          • **Rachas** - Mantén tu racha de ahorro diario
          
          ¡Compite contigo mismo y celebra tus logros!`,
        },
        {
          title: 'Crear una meta',
          content: `1. Ve a **Metas de Ahorro** → **+ Nueva Meta**
          2. Dale un nombre (ej: "Vacaciones Japón")
          3. Define el objetivo (ej: 3.000€)
          4. Selecciona un icono (viaje, casa, coche...)
          5. Fecha límite (opcional)
          6. ¡Empieza a ahorrar!`,
        },
        {
          title: 'Badges disponibles',
          content: `🎯 **Primera Meta** - Crea tu primera meta (+50 XP)
          🐷 **Ahorrador Novato** - Ahorra 100€ (+100 XP)
          🏆 **Ahorrador Experto** - Ahorra 1.000€ (+250 XP)
          👑 **Ahorrador Maestro** - Ahorra 10.000€ (+500 XP)
          🔥 **Racha Semanal** - 7 días seguidos (+75 XP)
          ⚡ **Racha Mensual** - 30 días seguidos (+200 XP)
          ⭐ **Meta Cumplida** - Completa una meta (+150 XP)
          🏅 **Conquistador** - Completa 5 metas (+400 XP)`,
        },
      ],
    },
    'investments': {
      title: 'Inversiones',
      icon: TrendingUp,
      sections: [
        {
          title: 'Añadir inversiones',
          content: `Registra tu cartera de inversión:
          
          1. Ve a **Inversiones** → **+ Nueva Inversión**
          2. Tipo: Acciones, ETFs, Criptomonedas, Fondos
          3. Símbolo (ej: AAPL, BTC, SPY)
          4. Cantidad y precio de compra
          5. Guarda
          
          Los precios se actualizan automáticamente.`,
        },
        {
          title: 'Seguimiento automático',
          content: `TREBOL actualiza los precios de:
          
          • **Acciones** - NYSE, NASDAQ, BME (bolsa española)
          • **ETFs** - Principales fondos cotizados
          • **Criptomonedas** - Bitcoin, Ethereum y más
          • **Fondos de inversión** - Los más populares
          
          Actualización cada hora durante mercado abierto.`,
        },
        {
          title: 'Análisis de cartera',
          content: `Visualiza tu cartera:
          
          • **Distribución** - Gráfico por tipo de activo
          • **Rendimiento** - Ganancia/pérdida por inversión
          • **Histórico** - Evolución en el tiempo
          • **Diversificación** - Análisis de riesgo`,
        },
      ],
    },
    'multicurrency': {
      title: 'Multi-Divisa',
      icon: Globe,
      sections: [
        {
          title: 'Conversor de divisas',
          content: `Convierte entre 15 monedas en tiempo real:
          
          EUR, USD, GBP, CHF, JPY, CAD, AUD, MXN, BRL, ARS, CLP, COP, CNY, INR, KRW
          
          Las tasas se actualizan diariamente.`,
        },
        {
          title: 'Configuración de moneda',
          content: `1. Ve a **Multi-Divisa** → **Configuración**
          2. Selecciona tu moneda principal
          3. Activa "Conversión automática" si lo deseas
          4. "Mostrar moneda original" mantiene el valor original visible
          
          Todos los totales se mostrarán en tu moneda principal.`,
        },
      ],
    },
    'tax': {
      title: 'Integración Fiscal',
      icon: Landmark,
      sections: [
        {
          title: 'IRPF Personal',
          content: `Calcula tu IRPF estimado:
          
          • Visualiza tus ingresos anuales
          • Gastos deducibles
          • Tramos impositivos actualizados
          • Estimación de impuestos
          
          ⚠️ Esta información es orientativa. Consulta un asesor fiscal.`,
        },
        {
          title: 'Autónomos',
          content: `Calculadora para Modelo 130 y 303:
          
          1. Introduce ingresos y gastos del trimestre
          2. Selecciona retención IRPF (7%, 15%, 20%)
          3. Selecciona tipo de IVA
          4. Obtén el cálculo de IRPF e IVA a pagar
          
          **Fechas de presentación:**
          • 1T: 1-20 Abril
          • 2T: 1-20 Julio
          • 3T: 1-20 Octubre
          • 4T: 1-30 Enero`,
        },
        {
          title: 'Gastos deducibles',
          content: `Si eres autónomo, puedes deducir:
          
          • **Suministros (30%)** - Luz, agua, gas, internet
          • **Material de oficina** - Papelería, consumibles
          • **Equipos** - Ordenadores, móviles (uso profesional)
          • **Formación** - Cursos relacionados con tu actividad
          • **Transporte** - Desplazamientos profesionales
          • **Seguros** - RC, autónomos
          • **Gestoría** - Asesoría fiscal
          • **Cuota de autónomos** - Seguridad Social`,
        },
      ],
    },
    'ai-assistant': {
      title: 'Asistente IA',
      icon: Brain,
      sections: [
        {
          title: 'Cómo usar el asistente',
          content: `El asistente de IA te ayuda con tus finanzas:
          
          1. Ve a **Asistente**
          2. Escribe tu pregunta
          3. Obtén respuestas personalizadas basadas en TUS datos
          
          **Ejemplos de preguntas:**
          • "¿Cuánto he gastado en restaurantes este mes?"
          • "¿Cómo puedo ahorrar más?"
          • "¿En qué categoría gasto más?"
          • "Dame consejos para reducir gastos"`,
        },
        {
          title: 'Comandos de voz',
          content: `Usa tu voz para interactuar:
          
          1. Ve a **Voz**
          2. Pulsa el botón de micrófono
          3. Di tu comando
          
          **Comandos soportados:**
          • "¿Cuánto he gastado este mes?"
          • "¿Cuál es mi balance?"
          • "Ir a transacciones"
          • "¿Cómo va mi presupuesto de alimentación?"`,
        },
      ],
    },
    'alerts': {
      title: 'Alertas y Notificaciones',
      icon: Bell,
      sections: [
        {
          title: 'Tipos de alertas',
          content: `Configura alertas personalizadas:
          
          • **Presupuesto** - Cuando superes un % del límite
          • **Gasto grande** - Transacciones superiores a X€
          • **Gasto inusual** - La IA detecta anomalías
          • **Pagos recurrentes** - Aviso X días antes
          • **Metas de ahorro** - Progreso de tus objetivos
          • **Resumen semanal** - Cada lunes
          • **Saldo bajo** - Cuando una cuenta baje de X€`,
        },
        {
          title: 'Notificaciones push',
          content: `Recibe alertas en tu dispositivo:
          
          1. Ve a **Configuración** → **Notificaciones Push**
          2. Pulsa "Activar"
          3. Acepta el permiso del navegador
          4. Configura qué notificaciones recibir
          
          **Funciona en:** Chrome, Firefox, Safari, Edge (móvil y escritorio)`,
        },
      ],
    },
    'security': {
      title: 'Seguridad',
      icon: Shield,
      sections: [
        {
          title: 'Autenticación 2FA',
          content: `Protege tu cuenta con verificación en dos pasos:
          
          1. Ve a **Configuración** → **Seguridad**
          2. Pulsa "Activar 2FA"
          3. Escanea el código QR con Google Authenticator o Authy
          4. Introduce el código de 6 dígitos
          5. Guarda los códigos de respaldo
          
          ⚠️ Los códigos de respaldo son tu última opción si pierdes acceso.`,
        },
        {
          title: 'Exportar/Eliminar datos (GDPR)',
          content: `Tienes control total sobre tus datos:
          
          • **Exportar datos** - Descarga todo en JSON
          • **Eliminar cuenta** - Borra todos tus datos permanentemente
          
          Ve a **Configuración** → **Gestión de Cuenta**`,
        },
        {
          title: 'Recuperar contraseña',
          content: `Si olvidaste tu contraseña:
          
          1. En la pantalla de login, pulsa "¿Olvidaste tu contraseña?"
          2. Introduce tu email
          3. Recibirás un enlace para crear nueva contraseña
          4. El enlace expira en 1 hora
          
          Si no recibes el email, revisa spam o contacta soporte.`,
        },
      ],
    },
    'mobile': {
      title: 'App Móvil (PWA)',
      icon: Smartphone,
      sections: [
        {
          title: 'Instalar en iPhone',
          content: `TREBOL funciona como una app nativa:
          
          1. Abre TREBOL en Safari
          2. Pulsa el botón de compartir (cuadrado con flecha)
          3. Selecciona "Añadir a pantalla de inicio"
          4. Dale nombre y pulsa "Añadir"
          
          ¡Ya tienes TREBOL como app con icono!`,
        },
        {
          title: 'Instalar en Android',
          content: `1. Abre TREBOL en Chrome
          2. Verás un banner "Instalar app" o menú (⋮)
          3. Pulsa "Instalar" o "Añadir a pantalla de inicio"
          4. Confirma
          
          La app se descargará y aparecerá en tu launcher.`,
        },
        {
          title: 'Funciona offline',
          content: `TREBOL guarda datos localmente:
          
          • **Dashboard** - Datos cacheados
          • **Transacciones** - Las últimas 100
          • **Configuración** - Siempre disponible
          
          Los cambios offline se sincronizan al recuperar conexión.`,
        },
      ],
    },
    'plans': {
      title: 'Planes y Precios',
      icon: CreditCard,
      sections: [
        {
          title: 'Plan Gratuito',
          content: `Incluye:
          
          ✓ 50 transacciones/mes
          ✓ 2 cuentas bancarias
          ✓ 3 presupuestos
          ✓ Categorías ilimitadas
          ✓ Asistente IA (10 consultas/mes)
          ✓ Escaneo recibos (5/mes)`,
        },
        {
          title: 'Plan Premium (€7.99/mes)',
          content: `Todo lo gratuito más:
          
          ✓ Transacciones ilimitadas
          ✓ Cuentas ilimitadas
          ✓ Presupuestos ilimitados
          ✓ Asistente IA ilimitado
          ✓ Escaneo recibos ilimitado
          ✓ Exportar informes PDF/Excel
          ✓ Modo Familia/Pareja
          ✓ Inversiones tracking
          ✓ Soporte prioritario
          
          **-20% con facturación anual**`,
        },
        {
          title: 'Plan Business (€19.99/mes)',
          content: `Todo Premium más:
          
          ✓ Multi-empresa
          ✓ Facturación integrada
          ✓ Open Banking conexión
          ✓ Importación bancaria automática
          ✓ Informes fiscales
          ✓ API acceso
          ✓ Usuarios equipo (hasta 5)
          ✓ Auditoría completa
          ✓ Soporte dedicado 24/7
          ✓ Onboarding personalizado`,
        },
      ],
    },
  };

  // FAQ
  const faq = [
    {
      question: '¿Mis datos están seguros?',
      answer: 'Sí. Usamos cifrado AES-256 para datos en reposo y TLS 1.3 para transmisión. Nunca vendemos ni compartimos tus datos. Cumplimos con GDPR.',
    },
    {
      question: '¿Puedo usar TREBOL gratis?',
      answer: 'Sí, el plan gratuito incluye funciones básicas sin límite de tiempo. Puedes actualizar a Premium cuando quieras.',
    },
    {
      question: '¿Cómo cancelo mi suscripción?',
      answer: 'Ve a Configuración → Planes → Cancelar suscripción. Seguirás teniendo acceso hasta el fin del período pagado.',
    },
    {
      question: '¿TREBOL se conecta a mi banco automáticamente?',
      answer: 'La función Open Banking está en desarrollo. Por ahora, puedes importar extractos CSV/OFX manualmente.',
    },
    {
      question: '¿Funciona en todos los dispositivos?',
      answer: 'Sí, TREBOL es una PWA que funciona en cualquier navegador moderno: Chrome, Firefox, Safari, Edge, en móvil y escritorio.',
    },
    {
      question: '¿Cómo contacto con soporte?',
      answer: 'Email: soporte@trebolfinance.com. Usuarios Premium tienen chat en vivo y respuesta prioritaria.',
    },
    {
      question: '¿Puedo exportar mis datos?',
      answer: 'Sí, ve a Configuración → Gestión de Cuenta → Exportar Datos. Recibirás un JSON con toda tu información.',
    },
    {
      question: '¿Qué pasa si pierdo mi teléfono?',
      answer: 'Tus datos están en la nube. Inicia sesión desde cualquier dispositivo. Recomendamos activar 2FA para mayor seguridad.',
    },
  ];

  // Filtrar contenido por búsqueda
  const filterContent = (section) => {
    if (!searchQuery) return section.sections;
    const query = searchQuery.toLowerCase();
    return section.sections.filter(
      s => s.title.toLowerCase().includes(query) || s.content.toLowerCase().includes(query)
    );
  };

  const filteredFaq = faq.filter(
    f => f.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
         f.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6 pb-8" data-testid="help-center-page">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-2xl font-bold text-foreground flex items-center justify-center gap-2">
          <HelpCircle className="text-primary" />
          Centro de Ayuda
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Manual completo de TREBOL Finance
        </p>
      </div>

      {/* Search */}
      <div className="relative max-w-xl mx-auto">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={18} />
        <Input
          type="text"
          placeholder="Buscar en la ayuda..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="help-search"
        />
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-4xl mx-auto">
        {Object.entries(userManual).slice(0, 8).map(([key, section]) => {
          const Icon = section.icon;
          return (
            <Button
              key={key}
              variant="outline"
              className="flex flex-col items-center gap-2 h-auto py-4"
              onClick={() => setActiveSection(key)}
            >
              <Icon size={20} className="text-primary" />
              <span className="text-xs">{section.title}</span>
            </Button>
          );
        })}
      </div>

      {/* Tabs */}
      <Tabs defaultValue="manual" className="max-w-4xl mx-auto">
        <TabsList className="grid w-full grid-cols-1 sm:grid-cols-3 h-auto gap-1 sm:gap-0">
          <TabsTrigger value="manual">
            <Book size={16} className="mr-2" />
            Manual
          </TabsTrigger>
          <TabsTrigger value="faq">
            <MessageCircle size={16} className="mr-2" />
            FAQ
          </TabsTrigger>
          <TabsTrigger value="contact">
            <Mail size={16} className="mr-2" />
            Contacto
          </TabsTrigger>
        </TabsList>

        {/* Manual Tab */}
        <TabsContent value="manual" className="space-y-4">
          <div className="grid md:grid-cols-4 gap-4">
            {/* Sidebar */}
            <Card className="md:col-span-1">
              <CardContent className="p-2">
                <nav className="space-y-1">
                  {Object.entries(userManual).map(([key, section]) => {
                    const Icon = section.icon;
                    return (
                      <button
                        key={key}
                        onClick={() => setActiveSection(key)}
                        className={cn(
                          "w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors text-left",
                          activeSection === key
                            ? "bg-primary text-primary-foreground"
                            : "hover:bg-muted"
                        )}
                      >
                        <Icon size={16} />
                        {section.title}
                      </button>
                    );
                  })}
                </nav>
              </CardContent>
            </Card>

            {/* Content */}
            <Card className="md:col-span-3">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {React.createElement(userManual[activeSection]?.icon || HelpCircle, { size: 20, className: "text-primary" })}
                  {userManual[activeSection]?.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="w-full">
                  {filterContent(userManual[activeSection] || { sections: [] }).map((item, idx) => (
                    <AccordionItem key={idx} value={`item-${idx}`}>
                      <AccordionTrigger className="text-left">
                        {item.title}
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="prose prose-sm dark:prose-invert max-w-none">
                          {item.content.split('\n').map((line, i) => (
                            <p key={i} className="mb-2 whitespace-pre-wrap">
                              {line}
                            </p>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* FAQ Tab */}
        <TabsContent value="faq">
          <Card>
            <CardHeader>
              <CardTitle>Preguntas Frecuentes</CardTitle>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="w-full">
                {filteredFaq.map((item, idx) => (
                  <AccordionItem key={idx} value={`faq-${idx}`}>
                    <AccordionTrigger className="text-left">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent>
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Contact Tab */}
        <TabsContent value="contact">
          <div className="grid md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="text-primary" />
                  Email
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  Escríbenos y te responderemos en menos de 24 horas.
                </p>
                <a 
                  href="mailto:soporte@trebolfinance.com"
                  className="text-primary hover:underline font-medium"
                >
                  soporte@trebolfinance.com
                </a>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageCircle className="text-primary" />
                  Chat en Vivo
                </CardTitle>
                <Badge variant="outline">Premium</Badge>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  Usuarios Premium tienen acceso a chat en vivo con soporte técnico.
                </p>
                <Button disabled={user?.plan !== 'premium'}>
                  Iniciar Chat
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Tips */}
      <Card className="max-w-4xl mx-auto bg-gradient-to-r from-primary/10 to-primary/5">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
              <Lightbulb className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h4 className="font-semibold text-foreground">¿Sabías que...?</h4>
              <p className="text-sm text-muted-foreground mt-1">
                Puedes usar atajos de teclado para navegar más rápido. Pulsa <kbd className="px-1 bg-muted rounded">?</kbd> en cualquier momento para ver la lista completa de atajos.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default HelpCenter;
